import * as React from "react";
import Axios from "axios";
import { Alert, Grid, Paper, Typography } from "@mui/material";
import Title from "@/components/molecules/Title";
import { ActionButtons } from "@/components/common/buttonGroup/ActionButtons";
import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import toast from "@/components/hoc/SnackbarAlert";
import { useLoadingPromise } from "@/hooks/useLoadingPromise";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import { getOfficesAPI } from "@/util/api/API";
import { ONLY_CONTRACT } from "@/models/office";
import {
  createAccidentReportPersonAPI,
  getAccidentReportPersonRegistrationLimitAPI,
} from "@/util/api/accidentReportPersons";
import {
  hasFormError,
  validateAccidentReportPersonForm,
  type AccidentReportPersonFormError,
  type AccidentReportPersonFormInput,
} from "@/models/accidentReportPersons";
import type { Office } from "@/models/office";
import { AccidentReportPersonsConstants as Constants } from "./AccidentReportPersonsConstants";
import type { AccidentReportPersonsSearchState } from "./AccidentReportPersons";
import { AccidentReportPersonsCreateForm } from "@/components/accidentReportPersons/AccidentReportPersonsCreateForm";

const LIST_PATH = "/accident_report_persons";

const emptyInput: AccidentReportPersonFormInput = {
  name: "",
  email: "",
  phoneNumber: "",
  officeId: null,
};

/**
 * 事故報告責任者の新規登録（事業者向け）。
 *
 * 登録上限は次の3タイミングで判定する。
 * 1. 画面を開いた直後（一覧で選択中の営業所で判定）
 * 2. 会社／営業所を変更した時点（変更後の営業所で再判定）
 * 1・2 は文言を表示して登録ボタンを非活性にする。
 * 3. 登録実行時（サーバ側で再判定）。超過していた場合はポップアップで文言を表示し、登録しない。
 *
 * 氏名の重複チェック（同一営業所の登録中に同姓同名がいる場合は登録不可）もサーバ側で判定し、
 * エラーメッセージをポップアップで表示する。
 */
export const AccidentReportPersonsCreate = (): JSX.Element => {
  const { navigate, savedState } =
    useStatefulNavigator<AccidentReportPersonsSearchState>();

  const [offices, setOffices] = React.useState<Office[]>([]);
  const [inputData, setInputData] =
    React.useState<AccidentReportPersonFormInput>(emptyInput);
  const [officeSelected, setOfficeSelected] = React.useState<Office | null>(
    null,
  );
  const [errorData, setErrorData] =
    React.useState<AccidentReportPersonFormError>({});
  const [blockedMessage, setBlockedMessage] = React.useState<string | null>(
    null,
  );
  // 選択中の営業所が登録上限に達している場合に true（登録ボタンを非活性にする）
  const [limitReached, setLimitReached] = React.useState(false);
  const [registerAsMlitFlow, setRegisterAsMlitFlow] = React.useState(false);
  // 営業所を素早く切り替えたときに古いレスポンスで上書きしないよう、最後にリクエストした営業所IDを保持する
  const latestLimitCheckOfficeId = React.useRef<number | null>(null);

  const [creating, createPerson] = useLoadingPromise(
    createAccidentReportPersonAPI,
  );

  const goToList = () => navigate(LIST_PATH, savedState);

  const checkLimit = React.useCallback(async (officeId: number) => {
    latestLimitCheckOfficeId.current = officeId;
    try {
      const response =
        await getAccidentReportPersonRegistrationLimitAPI(officeId);
      // 応答が返るまでの間に別の営業所へ切り替えられていたら、古い応答は無視する
      if (latestLimitCheckOfficeId.current !== officeId) {
        return;
      }
      setLimitReached(response.data.limitReached);
    } catch (error) {
      // 判定に失敗した場合は登録を塞がない。登録実行時にサーバ側で再判定される
      console.error("==Error when check registration limit==", error);
      if (latestLimitCheckOfficeId.current === officeId) {
        setLimitReached(false);
      }
    }
  }, []);

  React.useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await getOfficesAPI(ONLY_CONTRACT);
        const fetched = response.data ?? [];
        setOffices(fetched);

        // 初期値は一覧画面で選択中の営業所
        const initial = fetched.find(
          (office) => office.id === savedState?.officeId,
        );
        if (initial) {
          setOfficeSelected(initial);
          setInputData((current) => ({ ...current, officeId: initial.id }));
          checkLimit(initial.id);
        }
      } catch (error) {
        console.error("==Error when fetch offices==", error);
      }
    };
    fetchOffices();
    // 初回のみ実行する（savedState は遷移時に確定しているため依存に含めない）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeData = (
    key: keyof AccidentReportPersonFormInput,
    value: string,
  ) => {
    setInputData({ ...inputData, [key]: value });
    setErrorData({ ...errorData, [key]: undefined });
  };

  const onChangeOffice = (value: Office | null) => {
    setOfficeSelected(value);
    setInputData({ ...inputData, officeId: value?.id ?? null });
    setErrorData({ ...errorData, officeId: undefined });
    setLimitReached(false);
    if (value) {
      checkLimit(value.id);
    }
  };

  const onSubmit = async () => {
    const nextErrors = validateAccidentReportPersonForm(inputData);
    if (hasFormError(nextErrors)) {
      setErrorData(nextErrors);
      return;
    }
    setErrorData({});

    try {
      await createPerson({
        officeId: inputData.officeId as number,
        name: inputData.name.trim(),
        email: inputData.email.trim(),
        phoneNumber: inputData.phoneNumber.trim(),
        registerAsMlitFlowPerson: registerAsMlitFlow,
      });
      toast.success(Constants.RegisterSuccess);
      goToList();
    } catch (error) {
      // 登録上限・氏名重複はサーバ側で判定し、返ってきた文言をそのままポップアップで表示する
      if (Axios.isAxiosError(error) && error.response?.data?.message) {
        setBlockedMessage(String(error.response.data.message));
        return;
      }
      console.error("==Error when create accidentReportPerson==", error);
    }
  };

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title>{Constants.CreateTitle}</Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        {/* 他の画面と同じく、内容は入れ子の Grid container に入れる（左端をそろえるため） */}
        <Grid container spacing={2}>
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              {limitReached && (
                <Alert severity="warning" sx={{ m: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {Constants.RegistrationLimitMessage}
                  </Typography>
                </Alert>
              )}

              <AccidentReportPersonsCreateForm
                inputData={inputData}
                offices={offices}
                officeSelected={officeSelected}
                errorData={errorData}
                disabled={creating}
                onChangeData={onChangeData}
                onChangeOffice={onChangeOffice}
                registerAsMlitFlow={registerAsMlitFlow}
                onChangeRegisterAsMlitFlow={setRegisterAsMlitFlow}
              />
              {/* 上限に達している営業所では登録ボタンを押せないようにする（ActionButtonsはloadingで非活性になる） */}
              <ActionButtons
                forSave
                loading={creating || limitReached}
                onCancel={goToList}
                onSubmit={onSubmit}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      <ConfirmDialog
        visible={!!blockedMessage}
        headerTitle={Constants.CannotRegisterTitle}
        headerType="Delete"
        type="Normal"
        showCancelBtn={false}
        textAlign="left"
        onOk={() => setBlockedMessage(null)}
        onCancel={() => setBlockedMessage(null)}
      >
        <Typography sx={{ whiteSpace: "pre-wrap" }}>
          {blockedMessage}
        </Typography>
      </ConfirmDialog>
    </>
  );
};
